import { err, ok, type Result } from '../result.ts';

// Visit diagnoses (docs/plans/phase1-consultation.md §2.4). A diagnosis is never edited:
// changing its rank, certainty or code writes a new current row that replaces the old
// one, which becomes `corrected`. One current primary per visit.

export const DIAGNOSIS_RANKS = ['primary', 'secondary'] as const;
export type DiagnosisRank = (typeof DIAGNOSIS_RANKS)[number];

/**
 * FHIR Condition verificationStatus values. `refuted` reads "Ruled out".
 * TODO(clinical-verify): C15, the certainty values (accepted default 25 Sep 2026).
 */
export const DIAGNOSIS_CERTAINTIES = ['provisional', 'confirmed', 'refuted'] as const;
export type DiagnosisCertainty = (typeof DIAGNOSIS_CERTAINTIES)[number];

export const DIAGNOSIS_STATUSES = ['current', 'corrected', 'entered_in_error'] as const;
export type DiagnosisStatus = (typeof DIAGNOSIS_STATUSES)[number];

export const DIAGNOSIS_CERTAINTY_LABELS: Record<DiagnosisCertainty, string> = {
  provisional: 'Provisional',
  confirmed: 'Confirmed',
  refuted: 'Ruled out',
};

export interface DiagnosisFact {
  id: string;
  rank: DiagnosisRank;
  certainty: DiagnosisCertainty;
  status: DiagnosisStatus;
}

export type DiagnosisChangeErrorCode =
  'diagnosis.primary_exists' | 'diagnosis.ruled_out_primary' | 'diagnosis.not_current';

/**
 * Whether a new diagnosis (or a correction replacing `replacesId`) keeps the visit's
 * diagnoses consistent: one current primary, and a ruled-out diagnosis is never primary.
 * TODO(clinical-verify): C15, a ruled-out diagnosis can't be the primary.
 */
export function checkDiagnosisChange(
  existing: readonly DiagnosisFact[],
  change: { rank: DiagnosisRank; certainty: DiagnosisCertainty; replacesId?: string },
): Result<void, DiagnosisChangeErrorCode> {
  if (change.replacesId !== undefined) {
    const target = existing.find((e) => e.id === change.replacesId);
    if (target?.status !== 'current') return err('diagnosis.not_current');
  }
  if (change.rank === 'primary' && change.certainty === 'refuted') {
    return err('diagnosis.ruled_out_primary');
  }
  if (change.rank === 'primary') {
    const other = existing.find(
      (e) => e.status === 'current' && e.rank === 'primary' && e.id !== change.replacesId,
    );
    if (other !== undefined) return err('diagnosis.primary_exists');
  }
  return ok(undefined);
}

/**
 * Whether a diagnosis counts: for "has a diagnosis" at finish, and for MOH reports and
 * claims. Ruled-out, corrected and entered-in-error rows don't.
 */
export function countsAsDiagnosis(d: Pick<DiagnosisFact, 'certainty' | 'status'>): boolean {
  return d.status === 'current' && d.certainty !== 'refuted';
}

export function hasCountedDiagnosis(
  diagnoses: readonly Pick<DiagnosisFact, 'certainty' | 'status'>[],
): boolean {
  return diagnoses.some(countsAsDiagnosis);
}
